import React, { Component } from 'react';
import { Link } from 'react-router';
import PaymentUtils from '../util/PaymentUtils'
import BooleanUtils from '../util/BooleanUtils'
import '../css/components/locationListing.css';
import '../css/theme/buttons.css';
import Busy from "./Busy";
import ConfirmDialog from "./ConfirmDialog";
import { deactivateLocation as deactivateLocationRequest, deleteLocation, sendOfflineNotification, getActiveBookingsForLocation } from './location/requests/location-requests';
import { formatPhoneNumber } from '../util/ValidationUtils';
import {externalLinks} from '../requests/external-endpoints';
import ImageComponent from "./skeleton/ImageComponent";
import {constructListingImageUrl} from "../util/ConstructImageUrlUtil";
import { hasAdminAccess, hasOwnerAccess, hasGateManagerAccess } from "../util/AccountUtils";
import {getErrorMessageForNonStandardAndStandardResponse} from "../util/NetworkErrorUtil";
import { LOCATION_API_STATUS } from "../types/LocationStatus";
import { Link as RouterLink } from 'react-router';

const $ = window.$;

class LocationListing extends Component {
    constructor(props) {
        super(props);

        this.state = {
            showDeactivateConfirmation: false,
            showDeleteConfirmation: false,
            showActiveBookingsBlock: false,
            showAdminActiveBookingsWarning: false,
            checkingActiveBookings: false
        };

        this.isEmailVerified = props.isEmailVerified ?? true;
    }

    handleLocationEditClick = () => {
        this.props.handleEditLocation(this.props.location);
        this.props.openEditModal()
    };

    handleLocationDeleteClick = () => {
        if (!this.canDeleteLocation()) {
            return;
        }
        Busy.set(false);
        this.setState({
            showDeleteConfirmation: true
        });
    }

    canDeactivateLocation = () => {
        // Gate managers (in addition to owners and admins) may deactivate a location.
        return hasGateManagerAccess(this.props.account);
    }

    canDeleteLocation = () => {
        if (!hasOwnerAccess(this.props.account)) {
            return false;
        }
        // Hide the delete action for statuses the API will never allow deleting:
        // only DRAFT or deactivated (APPROVED & not live) locations are eligible,
        // and never active or in-review ones. The API additionally rejects
        // deleting a location that still has bookings; that check is enforced
        // server-side and surfaced via the failure toast.
        const { status, live } = this.props.location;
        const isDraft = status === LOCATION_API_STATUS.DRAFT;
        const isDeactivated = status === LOCATION_API_STATUS.APPROVED && !live;
        return isDraft || isDeactivated;
    }

    startDeactivateProcess = async () => {
        if (!this.canDeactivateLocation()) {
            return;
        }

        sendOfflineNotification(this.props.location.id).catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Failed to send offline notification for location', this.props.location?.id, error);
        });

        this.setState({ checkingActiveBookings: true });
        Busy.set(true);
        try {
            const res = await getActiveBookingsForLocation(this.props.location.id);
            const totalElements = res.body?.count || 0;
            Busy.set(false);

            if (totalElements > 0) {
                if (hasAdminAccess(this.props.account)) {
                    this.setState({
                        showAdminActiveBookingsWarning: true,
                        checkingActiveBookings: false
                    });
                } else {
                    this.setState({
                        showActiveBookingsBlock: true,
                        checkingActiveBookings: false
                    });
                }
            } else {
                this.setState({
                    showDeactivateConfirmation: true,
                    checkingActiveBookings: false
                });
            }
        } catch (error) {
            Busy.set(false);
            this.setState({ checkingActiveBookings: false });
            // eslint-disable-next-line no-console
            console.error('Failed to check active bookings', error);
            window.alert('Unable to verify whether this location has active bookings. Please try again.');
        }
    };

    cancelDeactivateProcess = () => {
        Busy.set(false);
        this.setState({
            showDeactivateConfirmation: false,
            showActiveBookingsBlock: false,
            showAdminActiveBookingsWarning: false
        });
    };

    cancelDeleteProcess = () => {
        Busy.set(false);
        this.setState({
            showDeleteConfirmation: false
        });
    }

    deactivateLocation = async () => {
        if (!this.canDeactivateLocation()) {
            return;
        }

        try {
            await deactivateLocationRequest(this.props.location.id)
            this.handleDeactivateLocationSuccess()
        } catch (err) {
            this.handleDeactivateLocationFailure(err)
        }

        Busy.set(false)
    }

    deleteLocation = async () => {
        if (!this.canDeleteLocation()) {
            return;
        }
        const locationId = this.props.location.id;
        try {
            await deleteLocation(locationId)
            this.handleDeleteLocationSuccess()
        } catch (err) {
            this.handleDeleteLocationFailure(err)
        }

        Busy.set(false)
    }

    handleDeleteLocationSuccess = () => {
        Busy.set(false);
        this.setState({
            showDeleteConfirmation: false
        });
        this.props.handleDeleteLocationSuccess(this.props.location);
    }

    handleDeleteLocationFailure = (err) => {
        Busy.set(false);
        this.setState({
            showDeleteConfirmation: false
        });
        this.props.handleDeleteLocationFailure(getErrorMessageForNonStandardAndStandardResponse(err));
    }

    handleDeactivateLocationSuccess = () => {
        Busy.set(false);
        this.setState({
            showDeactivateConfirmation: false
        });
        this.props.handleDeactivateLocationSuccess(this.props.location);
    };

    handleDeactivateLocationFailure = (err) => {
        Busy.set(false);
        this.setState({
            showDeactivateConfirmation: false
        });
        this.props.handleDeactivateLocationFailure(getErrorMessageForNonStandardAndStandardResponse(err));
    };

    locationShowingInSearchResults() {
        return this.props.location.isVisible;
    }

    renderActiveBookingsBlockModal() {
        if (!this.state.showActiveBookingsBlock) return null;

        return (
            <ConfirmDialog
                showAlert={this.state.showActiveBookingsBlock}
                title="Cannot Deactivate Location"
                onClose={this.cancelDeactivateProcess}
                proceedEventHandler={this.cancelDeactivateProcess}
            >
                <p>
                    All bookings must be cancelled or inactivated before this location can be deactivated.
                </p>
                <p>
                    <RouterLink to={`/bookings/supplier-bookings?status=ACTIVE&locationId=${this.props.location.id}`}>
                        View all active bookings for this location
                    </RouterLink>
                </p>
                <p>
                    Need help? <a href={externalLinks.contact} target="_blank" rel="noopener noreferrer">Contact support by clicking here</a>.
                </p>
            </ConfirmDialog>
        );
    }

    renderAdminActiveBookingsWarningModal() {
        if (!this.state.showAdminActiveBookingsWarning) return null;

        return (
            <ConfirmDialog
                showAlert={this.state.showAdminActiveBookingsWarning}
                title="Warning: Active Bookings Exist"
                onClose={this.cancelDeactivateProcess}
                proceedEventHandler={this.deactivateLocation}
            >
                <p>
                    This location has active bookings. All bookings should be cancelled or inactivated before deactivating this location.
                </p>
                <p>
                    <RouterLink to={`/bookings/supplier-bookings?status=ACTIVE&locationId=${this.props.location.id}`}>
                        View all active bookings for this location
                    </RouterLink>
                </p>
                <p>
                    As an admin, you may override and proceed with deactivation.
                </p>
            </ConfirmDialog>
        );
    }

    getNotVisibleInSearchResultReason() {
        if (!this.props.location.locationName || !this.props.location.pricePerDay ||
            !this.props.location.addressLine1 || !this.props.location.city || !this.props.location.state
            || !this.props.location.zip) {
            return "Missing Required Fields";
        } else if (!this.props.location.locationEquipmentTypes || (this.props.location.locationEquipmentTypes && this.props.location.locationEquipmentTypes.length === 0)) {
            return "No Equipment Types Selected";
        } else if (!this.props.location.locationFeatures || (this.props.location.locationFeatures && this.props.location.locationFeatures.length === 0)) {
            return "No Features Selected";
        } else if (!this.props.location.live) {
            return "Approval Required";
        }
    }


    render() {
        let width = $(window).width();
        return (
            <div className="ss-booking-container">
                <div>
                    {
                        (width > 999) ?
                            <div className="booking-list-item">
                                <div className="for-img">
                                    <ImageComponent
                                      className={"h-100 w-100"}
                                      src={constructListingImageUrl(this.props.location.listingImageFileName)}
                                      alt={`Listing image for ${this.props.location.locationName}`}
                                    />
                                </div>
                                <div className="for-content">
                                    <div className="booking-list-title">
                                        <span>{this.props.location.locationName}</span>
                                        <span>
                                            <div className="ss-booking-button-container">
                                                {
                                                    this.locationShowingInSearchResults() && this.canDeactivateLocation() ?
                                                        <button className="ss-button-danger" onClick={this.startDeactivateProcess} disabled={this.state.checkingActiveBookings}>Deactivate
                                                        </button> : null
                                                }
                                                {this.canDeleteLocation() ?
                                                    <button
                                                      className="ss-button-danger"
                                                      onClick={this.handleLocationDeleteClick}
                                                    >
                                                        Delete
                                                    </button> : null
                                                }
                                                <button
                                                    className="ss-button-primary"
                                                    onClick={this.handleLocationEditClick}
                                                    disabled={!this.isEmailVerified}
                                                    style={!this.isEmailVerified ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
                                                    Edit
                                                </button>
                                                    {!this.isEmailVerified && (
                                                        <span style={{ fontSize: '12px', color: '#999' }}>Email verification required</span>
                                                    )}
                                            </div>
                                        </span>

                                        <ConfirmDialog showAlert={this.state.showDeactivateConfirmation}
                                            title="Deactivate Location"
                                            onClose={this.cancelDeactivateProcess}
                                            proceedEventHandler={this.deactivateLocation}>
                                            Deactivate means the Location will be offline. Customers will not be able to book space at this location. You will need to contact Customer Support to reactivate the location
                                        </ConfirmDialog>

                                        {this.renderActiveBookingsBlockModal()}

                                        {this.renderAdminActiveBookingsWarningModal()}

                                        <ConfirmDialog showAlert={this.state.showDeleteConfirmation}
                                                       title="Delete Location"
                                                       onClose={this.cancelDeleteProcess}
                                                       proceedEventHandler={this.deleteLocation}>
                                            Are you sure you want to delete this location <strong>{this.props.location?.locationName}</strong>? This action is permanent and cannot be undone.
                                        </ConfirmDialog>

                                    </div>
                                    <div>
                                        <div className="flex">
                                            <div>
                                                <fieldset>
                                                    <div
                                                        className="bg-icon icon-address">{this.props.location.addressLine1}
                                                        <br />
                                                        {
                                                            this.props.location.city && this.props.location.state && this.props.location.zip ?
                                                                this.props.location.city + ", " + this.props.location.state + " " + this.props.location.zip
                                                                :
                                                                ""
                                                        }
                                                    </div>
                                                </fieldset>
                                            </div>

                                            <div>
                                                <fieldset>
                                                    <div
                                                        className="bg-icon icon-phone">{formatPhoneNumber(this.props.location.phoneNumber)}</div>
                                                </fieldset>
                                            </div>

                                            <div>
                                                <fieldset>
                                                    <label>Search Status:</label>
                                                    <div>
                                                        {
                                                            this.locationShowingInSearchResults() ?
                                                                <div>
                                                                    <span className="green">
                                                                        <i className="fa fa-check"></i> Visible
                                                                    </span>
                                                                    <br />
                                                                    <span>
                                                                        <Link to={{
                                                                            pathname: '/location-profile/' + this.props.location.id,
                                                                        }}>View</Link>
                                                                    </span>
                                                                </div>
                                                                :
                                                                <div>
                                                                    <span className="grey">
                                                                        <i className='fa fa-close'></i>NOT VISIBLE</span>
                                                                    <br />
                                                                    <span> {this.getNotVisibleInSearchResultReason()}</span>
                                                                </div>
                                                        }
                                                    </div>
                                                </fieldset>
                                            </div>

                                            <div>
                                                <fieldset>
                                                    <label>number of spaces:</label>
                                                    <div>{this.props.location.managedSpaces}</div>
                                                </fieldset>
                                            </div>

                                            <div>
                                                <fieldset>
                                                    <label>Per Day:</label>
                                                    <div>
                                                        {
                                                            this.props.location.pricePerDay ?
                                                                '$' + PaymentUtils.convertSmallestSubUnitToMainUnit(this.props.location.pricePerDay, PaymentUtils.CURRENCY_US_DOLLAR)
                                                                :
                                                                ""
                                                        }
                                                    </div>
                                                </fieldset>
                                            </div>

                                            {hasAdminAccess(this.props.account) ?
                                                <div>
                                                    <fieldset>
                                                        <label>Per Week:</label>
                                                        <div>{
                                                            this.props.location.pricePerWeek ?
                                                                '$' + PaymentUtils.convertSmallestSubUnitToMainUnit(this.props.location.pricePerWeek, PaymentUtils.CURRENCY_US_DOLLAR)
                                                                :
                                                                ""
                                                        }</div>
                                                    </fieldset>
                                                </div>
                                                : ''}

                                            <div>
                                                <fieldset>
                                                    <label>Per Month:</label>
                                                    <div>{
                                                        this.props.location.pricePerMonth ?
                                                            '$' + PaymentUtils.convertSmallestSubUnitToMainUnit(this.props.location.pricePerMonth, PaymentUtils.CURRENCY_US_DOLLAR)
                                                            :
                                                            ""
                                                    }</div>
                                                </fieldset>
                                            </div>

                                            <div>
                                                <fieldset>
                                                    <label>Charge Overages:</label>
                                                    <div>{this.props.location.chargeOverages ? "Yes" : "No"}</div>
                                                </fieldset>
                                            </div>

                                            <div>
                                                <fieldset>
                                                    <label>Overage Rate:</label>
                                                    <div>{
                                                        this.props.location.overageRate ?
                                                            '$' + PaymentUtils.convertSmallestSubUnitToMainUnit(this.props.location.overageRate, PaymentUtils.CURRENCY_US_DOLLAR)
                                                            :
                                                            ""
                                                    }</div>
                                                </fieldset>
                                            </div>

                                            <div>
                                                <fieldset>
                                                    <label>Automated Management:</label>
                                                    <div>{BooleanUtils.convertToYesNo(this.props.location.managedLocation)}</div>
                                                </fieldset>
                                            </div>
                                            <div>
                                                <fieldset>
                                                    <label>QR Scanner Available:</label>
                                                    <div>{BooleanUtils.convertToYesNo(this.props.location.qrScannerAvailable)}</div>
                                                </fieldset>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                            :
                            <div className="booking-list-item">
                                <div className="row1 flex">
                                    <div className="for-img">
                                        <ImageComponent
                                          className={"h-100 w-100"}
                                          src={constructListingImageUrl(this.props.location.listingImageFileName)}
                                          alt={`Listing image for ${this.props.location.locationName}`}
                                        />
                                    </div>
                                    <div className="booking-list-title">
                                        <span>{this.props.location.locationName}</span>
                                        <div className="for-content">
                                            <div>
                                                <div className="flex">
                                                    <div>
                                                        <fieldset>
                                                            <div
                                                                className="bg-icon icon-address">{this.props.location.addressLine1}
                                                                <br />
                                                                {
                                                                    this.props.location.city && this.props.location.state && this.props.location.zip ?
                                                                        this.props.location.city + ", " + this.props.location.state + " " + this.props.location.zip
                                                                        :
                                                                        ""
                                                                }
                                                            </div>
                                                        </fieldset>
                                                    </div>
                                                    <div>
                                                        <fieldset>
                                                            <div
                                                                className="bg-icon icon-phone">{this.props.location.phoneNumber}</div>
                                                        </fieldset>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="row2 flex">

                                    <div>
                                        <fieldset>
                                            <label>Search Status:</label>
                                            <div>
                                                {
                                                    this.locationShowingInSearchResults() ?
                                                        <div>
                                                            <span className="green">
                                                                <i className="fa fa-check"></i> Visible
                                                            </span>
                                                            <br />
                                                            <span><Link to={{
                                                                pathname: '/search',
                                                                search: '?initLat=' + this.props.location.addressLatitude + '&initLng=' + this.props.location.addressLongitude + '&selectedSupplier=' + this.props.location.id
                                                            }}>View</Link>
                                                            </span>
                                                        </div>
                                                        :
                                                        <div>
                                                            <span className="grey">
                                                                <i className='fa fa-close'></i>NOT VISIBLE</span>
                                                            <br />
                                                            <span> {this.getNotVisibleInSearchResultReason()}</span>
                                                        </div>
                                                }
                                            </div>
                                        </fieldset>
                                    </div>

                                    <div>
                                        <fieldset>
                                            <label>number of spaces:</label>
                                            <div>{this.props.location.managedSpaces}</div>
                                        </fieldset>
                                    </div>
                                    <div>
                                        <fieldset>
                                            <label>Per Day:</label>
                                            <div>
                                                {
                                                    this.props.location.pricePerDay ?
                                                        '$' + PaymentUtils.convertSmallestSubUnitToMainUnit(this.props.location.pricePerDay, PaymentUtils.CURRENCY_US_DOLLAR)
                                                        :
                                                        ""
                                                }
                                            </div>
                                        </fieldset>
                                    </div>

                                    {hasAdminAccess(this.props.account) ?
                                        <div>
                                            <fieldset>
                                                <label>Per Week:</label>
                                                <div>{
                                                    this.props.location.pricePerWeek ?
                                                        '$' + PaymentUtils.convertSmallestSubUnitToMainUnit(this.props.location.pricePerWeek, PaymentUtils.CURRENCY_US_DOLLAR)
                                                        :
                                                        ""
                                                }</div>
                                            </fieldset>
                                        </div>
                                        : ''}

                                    <div>
                                        <fieldset>
                                            <label>Per Month:</label>
                                            <div>{
                                                this.props.location.pricePerMonth ?
                                                    '$' + PaymentUtils.convertSmallestSubUnitToMainUnit(this.props.location.pricePerMonth, PaymentUtils.CURRENCY_US_DOLLAR)
                                                    :
                                                    ""
                                            }</div>
                                        </fieldset>
                                    </div>

                                    <div>
                                        <fieldset>
                                            <label>Overage Rate:</label>
                                            <div>{
                                                this.props.location.overageRate ?
                                                    '$' + PaymentUtils.convertSmallestSubUnitToMainUnit(this.props.location.overageRate, PaymentUtils.CURRENCY_US_DOLLAR)
                                                    :
                                                    ""
                                            }</div>
                                        </fieldset>
                                    </div>

                                    <div>
                                        <fieldset>
                                            <label>Automated Management:</label>
                                            <div>{BooleanUtils.convertToYesNo(this.props.location.managedLocation)}</div>
                                        </fieldset>
                                    </div>
                                </div>
                                <div>
                                    <div className="ss-booking-button-container">
                                        {this.locationShowingInSearchResults() && this.canDeactivateLocation() ?
                                            <button className="ss-button-danger" onClick={this.startDeactivateProcess}>
                                                Deactivate
                                            </button> : null
                                        }
                                        {this.canDeleteLocation() ?
                                            <button className="ss-button-danger" onClick={this.handleLocationDeleteClick}>
                                                Delete
                                            </button> : null
                                        }
                                        <button
                                            className="ss-button-primary"
                                            onClick={this.handleLocationEditClick}
                                            disabled={!this.isEmailVerified}
                                            title={!this.isEmailVerified ? "Email verification required to edit" : ""}>
                                            Edit
                                        </button>
                                    </div>

                                    <ConfirmDialog showAlert={this.state.showDeactivateConfirmation}
                                        title="Deactivate Location"
                                        onClose={this.cancelDeactivateProcess}
                                        proceedEventHandler={this.deactivateLocation}>
                                        Are you sure you want to deactivate this location?
                                    </ConfirmDialog>

                                    <ConfirmDialog showAlert={this.state.showDeleteConfirmation}
                                        title="Delete Location"
                                        onClose={this.cancelDeleteProcess}
                                        proceedEventHandler={this.deleteLocation}>
                                        Are you sure you want to delete this location <strong>{this.props.location?.locationName}</strong>? This action is permanent and cannot be undone.
                                    </ConfirmDialog>

                                    {this.renderActiveBookingsBlockModal()}

                                    {this.renderAdminActiveBookingsWarningModal()}

                                </div>
                            </div>
                    }

                </div>
            </div>
        )
    }
}

export default LocationListing;
